import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiRequest } from "@/lib/queryClient";
import type { ShipmentInventoryPolicy } from "@shared/shipmentInventoryPolicy";
export type { ShipmentInventoryPolicy } from "@shared/shipmentInventoryPolicy";

async function readPolicy(response: Response): Promise<ShipmentInventoryPolicy> {
  const value = await response.json();
  if (!value || typeof value !== "object" || typeof value.enabled !== "boolean"
    || !Number.isSafeInteger(value.version) || value.version < 0
    || !(value.updatedAt === null || (typeof value.updatedAt === "string" && Number.isFinite(Date.parse(value.updatedAt))))
    || !(value.updatedBy === null || (Number.isSafeInteger(value.updatedBy) && value.updatedBy > 0))) {
    throw new Error("出貨扣料設定格式不正確，請重新讀取或聯絡管理員。");
  }
  return value;
}

const POLICY_URL = "/api/config/shipment-inventory";
export const SHIPMENT_INVENTORY_POLICY_KEY = ["shipment-inventory-policy"] as const;

export function useShipmentInventoryPolicy(enabled = true) {
  const queryClient = useQueryClient();
  const query = useQuery<ShipmentInventoryPolicy>({
    queryKey: SHIPMENT_INVENTORY_POLICY_KEY,
    enabled,
    staleTime: 0,
    retry: false,
    refetchOnMount: "always",
    queryFn: async () => {
      const response = await apiRequest("GET", POLICY_URL);
      return readPolicy(response);
    },
  });

  const update = useMutation({
    retry: false,
    mutationFn: async (change: { enabled: boolean; expectedVersion: number }) => {
      const response = await apiRequest("PATCH", POLICY_URL, change);
      return readPolicy(response);
    },
    onSuccess: (policy) => {
      queryClient.setQueryData(SHIPMENT_INVENTORY_POLICY_KEY, policy);
    },
    onError: async () => {
      // A lost response may follow a committed change. Drop the old display and
      // read the server again; never retry a configuration write automatically.
      await queryClient.resetQueries({ queryKey: SHIPMENT_INVENTORY_POLICY_KEY, exact: true });
    },
  });

  return { query, update };
}
